import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Play, Pause } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * KidsWatch — kid-safe video playback page.
 *
 * Replaces the main /watch/:id surface for anything accessed from the
 * kids section so children never see grown-up comments, ads, or
 * reporting UI. Only plays videos that have is_kids_safe=true; anything
 * else redirects back to /kids with a toast.
 */
export default function KidsWatch() {
  const { videoId } = useParams();
  const nav = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/videos/${videoId}`)
      .then((r) => {
        const v = r.data;
        if (!v?.is_kids_safe) {
          toast.error("This video isn't kid-safe. Back to Kids!");
          nav("/kids", { replace: true });
          return;
        }
        setVideo(v);
      })
      .catch(() => {
        toast.error("Video not found");
        nav("/kids", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [videoId, nav]);

  const src = video && (video.hls_url || video.embed_url || video.video_url);
  const isEmbed = src && /youtube|youtu\.be|vimeo|tiktok/i.test(src);

  if (loading) return <div className="pt-32 text-white/60 text-center">Loading…</div>;
  if (!video) return null;

  const isKidsPost = video.format === "kids_post" || video.format === "kids_community" || !src;

  return (
    <div data-testid="kids-watch-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-5xl mx-auto text-white">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <Link data-testid="kwatch-back" to="/kids" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Kids
        </Link>
        <img src="/kids-learn-play-logo.png" alt="" className="w-24 h-auto drop-shadow-xl" draggable={false} />
      </div>

      {/* Player */}
      <div className="relative bg-black rounded-2xl overflow-hidden ring-4 ring-white/20 shadow-2xl">
        {isKidsPost ? (
          // Lightweight visual "post" card — used for bot-generated kids_post
          // entries that don't have a real media URL. Still fills the player
          // slot so kids don't see a broken surface.
          <div className={`w-full aspect-video bg-gradient-to-br ${video.color || "from-pink-400 to-fuchsia-500"} flex flex-col items-center justify-center p-6`}>
            <div className="text-[14vmin] min-text-[88px] leading-none drop-shadow-2xl">{video.emoji || "⭐"}</div>
            <div className="font-display font-black text-3xl md:text-5xl text-white text-center drop-shadow-lg mt-4 tracking-tight">
              {video.title}
            </div>
          </div>
        ) : isEmbed ? (
          <iframe
            src={src}
            className="w-full aspect-video"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            title={video.title}
          />
        ) : (
          <video
            ref={videoRef}
            src={src}
            controls
            autoPlay
            playsInline
            className="w-full aspect-video bg-black"
            poster={video.thumbnail_url || video.poster_url}
          />
        )}
        {/* Kids brand watermark — bottom-right */}
        <img
          src="/kids-learn-play-logo.png"
          alt=""
          data-testid="kwatch-watermark"
          className="absolute bottom-3 right-3 w-16 md:w-20 h-auto opacity-90 drop-shadow-2xl pointer-events-none"
        />
      </div>

      {/* Meta below player */}
      <div className="mt-4 flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 data-testid="kwatch-title" className="font-display font-black text-2xl md:text-3xl tracking-tight">{video.title}</h1>
          <Link
            to={`/kids/timeline/${video.creator_id}`}
            data-testid="kwatch-creator"
            className="inline-flex items-center gap-2 mt-2 text-white/80 hover:text-white text-sm"
          >
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-500 flex items-center justify-center text-sm">
              {video.creator_avatar || "⭐"}
            </span>
            @{video.handle || video.creator_name}
          </Link>
        </div>
        <button
          data-testid="kwatch-like"
          onClick={() => setLiked((l) => !l)}
          className={`inline-flex items-center gap-1.5 font-bold px-4 py-2 rounded-full transition ${liked ? "bg-pink-500 text-white" : "bg-white/10 text-white hover:bg-white/20"}`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-white" : ""}`} /> {liked ? "Loved!" : "Love it"}
        </button>
      </div>

      {video.description && (
        <p className="mt-4 text-white/75 text-sm leading-relaxed whitespace-pre-wrap bg-black/20 rounded-xl p-4">
          {video.description}
        </p>
      )}

      <div className="mt-6 text-center">
        <Link to="/kids" data-testid="kwatch-more" className="inline-flex items-center gap-2 bg-yellow-300 hover:bg-yellow-200 text-black font-black px-5 py-2.5 rounded-full">
          More kids fun →
        </Link>
      </div>
    </div>
  );
}
